import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { format } from 'date-fns'
import { StringParam, useQueryParams, withDefault } from 'use-query-params'

import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useQuery } from '@percent/partner-dashboard/common/hooks'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import {
  AllocationsSortBy,
  Allocation,
  AllocationStatus
} from '@percent/partner-dashboard/services/walletBalance/walletBalanceService.types'
import {
  Badge,
  BadgeVariant,
  Card,
  Filter,
  Flex,
  Icon,
  Loader,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableProvider,
  TableRow,
  Tooltip,
  Typography
} from '@percent/lemonade'
import { formatMoney } from '@percent/utility'
import { getExpiryBadge } from './getExpiryBadge'
import styles from './EmployeeWallet.module.scss'

export type EmployeeWalletProps = Readonly<{
  accountId: string
}>

const isSortBy = (value: string | null | undefined): value is AllocationsSortBy =>
  value === 'fundedAt' || value === 'expiresAt' || value === 'amount' || value === 'remainingAmount'

const isSortDirection = (value: string | null | undefined): value is 'asc' | 'desc' =>
  value === 'asc' || value === 'desc'

const isAllocationStatus = (value: string | null | undefined): value is AllocationStatus =>
  value === 'active' || value === 'used' || value === 'expired'

const statusBadgeVariant: Record<AllocationStatus, BadgeVariant> = {
  active: 'positive',
  used: 'interactive',
  expired: 'default'
}

const statusLabel = {
  active: 'status.active',
  used: 'typography.employeeDetails.wallet.used',
  expired: 'typography.employeeDetails.wallet.expired'
} as const

export function EmployeeWallet({ accountId }: EmployeeWalletProps) {
  const { t } = useTranslation()
  const { walletBalanceService } = useServices()

  const [queryParams, setQueryParams] = useQueryParams({
    sort: withDefault(StringParam, 'expiresAt'),
    direction: withDefault(StringParam, 'asc'),
    status: StringParam
  })

  const sortBy: AllocationsSortBy = isSortBy(queryParams.sort) ? queryParams.sort : 'expiresAt'
  const sortDirection: 'asc' | 'desc' = isSortDirection(queryParams.direction) ? queryParams.direction : 'asc'
  const statusFilter: AllocationStatus | '' = isAllocationStatus(queryParams.status) ? queryParams.status : ''

  // Pass `mount = false` so useQuery does not auto-fetch on mount; the useEffect below is the
  // single source of fetches, covering both initial mount and any change to the query args.
  const [{ data, isLoading, errorMessage }, { query }] = useQuery(
    walletBalanceService.getAllocations,
    { accountId, sortBy, sortDirection, includeExpired: true, includeUsed: true },
    false
  )

  useEffect(() => {
    query({ accountId, sortBy, sortDirection, includeExpired: true, includeUsed: true })
  }, [accountId, sortBy, sortDirection, query])

  const handleRequestSort = (columnKey: string) => {
    if (columnKey !== 'expiresAt') return

    const isAsc = sortBy === 'expiresAt' && sortDirection === 'asc'
    setQueryParams({ sort: 'expiresAt', direction: isAsc ? 'desc' : 'asc' })
  }

  const columns: TableColumn[] = useMemo(
    () => [
      { id: 'amountSeeded', content: t('table.header.amountSeeded') },
      { id: 'amountRemaining', content: t('table.header.amountRemaining') },
      { id: 'fundedAt', content: t('table.header.seeded') },
      { id: 'expiresAt', content: t('table.header.expires'), isSortable: true },
      { id: 'status', content: t('table.header.status') }
    ],
    [t]
  )

  const statusFilterOptions = useMemo(
    () => [
      { label: t('status.all'), value: '' },
      { label: t(statusLabel.active), value: 'active' },
      { label: t(statusLabel.used), value: 'used' },
      { label: t(statusLabel.expired), value: 'expired' }
    ],
    [t]
  )

  const isInitialLoad = isLoading && !data

  if (isInitialLoad) {
    return <Loader />
  }

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  const allocations: Allocation[] = data?.allocations ?? []
  const visibleAllocations = statusFilter
    ? allocations.filter(allocation => allocation.status === statusFilter)
    : allocations
  const isFilteredEmpty = allocations.length > 0 && visibleAllocations.length === 0
  const totalBalance = data?.totalBalance ?? { amount: 0, currency: 'USD' }
  const now = new Date()

  return (
    <Flex direction="column" gap="md" className={styles.wallet}>
      <Card>
        <div className={styles.balanceCardInner}>
          <div className={styles.balanceBox}>
            <Flex direction="column" gap="sm">
              <Typography variant="default">{t('typography.employeeDetails.wallet.balance')}</Typography>
              <Typography variant="h3">{formatMoney(totalBalance, { hideDecimalsIfZero: true })}</Typography>
            </Flex>
          </div>
        </div>
      </Card>

      <Card>
        <Flex direction="column" gap="md" className={styles.tableCardInner}>
          <Flex justify="flex-end">
            <Filter
              compact
              label={t('table.header.status')}
              options={statusFilterOptions}
              selectedValue={statusFilter}
              onChange={value => setQueryParams({ status: value || undefined })}
              testId="wallet-status-filter"
            />
          </Flex>
          <TableProvider
            columns={columns}
            totalResults={visibleAllocations.length}
            isLoading={isLoading}
            illustration="no-results"
            emptyTableTitle={t(
              isFilteredEmpty
                ? 'typography.employeeDetails.wallet.emptyFilterTitle'
                : 'typography.employeeDetails.wallet.emptyTitle'
            )}
            emptyTableDescription={t(
              isFilteredEmpty
                ? 'typography.employeeDetails.wallet.emptyFilterDescription'
                : 'typography.employeeDetails.wallet.emptyDescription'
            )}
            handleRequestSort={handleRequestSort}
            sortByColumn={sortBy}
            sortDirection={sortDirection === 'asc' ? 'ASC' : 'DESC'}
          >
            <Table>
              <TableBody>
                {visibleAllocations.map(allocation => {
                  const badge = getExpiryBadge(allocation, now)

                  return (
                    <TableRow
                      key={`${allocation.fundedAt}-${allocation.fundingSourceId ?? 'none'}-${allocation.amount.amount}`}
                    >
                      <TableCell>
                        <Flex align="center" gap="xs">
                          <span>{formatMoney(allocation.amount, { hideDecimalsIfZero: true })}</span>
                          {allocation.fundingSourceName && (
                            <Tooltip
                              content={t('toolTip.workplaceGiving.fundingSource', {
                                fundingSourceName: allocation.fundingSourceName
                              })}
                            >
                              <span className={styles.fundingSourceIcon}>
                                <Icon name="info" size={5} color="gray400" />
                              </span>
                            </Tooltip>
                          )}
                        </Flex>
                      </TableCell>
                      <TableCell>{formatMoney(allocation.remainingAmount, { hideDecimalsIfZero: true })}</TableCell>
                      <TableCell>{format(new Date(allocation.fundedAt), 'MMM d, yyyy')}</TableCell>
                      <TableCell>
                        <Flex align="center" gap="xs">
                          <span>
                            {allocation.expiresAt
                              ? format(new Date(allocation.expiresAt), 'MMM d, yyyy')
                              : t('typography.employeeDetails.wallet.noExpiration')}
                          </span>
                          {badge.kind === 'warning' && (
                            <Badge variant="warning">
                              {t('typography.employeeDetails.wallet.daysLeft', { count: badge.daysLeft })}
                            </Badge>
                          )}
                        </Flex>
                      </TableCell>
                      <TableCell>
                        <Badge variant={statusBadgeVariant[allocation.status]}>
                          {t(statusLabel[allocation.status])}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableProvider>
        </Flex>
      </Card>
    </Flex>
  )
}
